import { useEffect, useMemo, useState } from 'react';
import { usePointsGroupQuery, usePointsGroupUpdateMutation } from '@shared/api/graphql/generated';
import { toast } from '@shared/ui';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { IVideoStream } from 'application/pages/PointsGroupsPage/types';
import { TEXTS } from '../../texts';

interface IUseSelectMasterVideoStreamViewModel {
  devicesGroupId: string;
}

export const useSelectMasterVideoStreamViewModel = ({ devicesGroupId }: IUseSelectMasterVideoStreamViewModel) => {
  const [masterVideoStreamId, setMasterVideoStreamId] = useState<string | number>('');

  const { data, loading } = usePointsGroupQuery({
    variables: { id: devicesGroupId! },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    skip: !devicesGroupId,
  });

  const [mutate, mutationResult] = usePointsGroupUpdateMutation();

  const updatePointsGroup = async () => {
    if (devicesGroupId && masterVideoStreamId) {
      const result = await mutate({
        variables: { input: { devicesGroupId, masterVideoStreamId: String(masterVideoStreamId) } },
      });

      if (result.data?.smartlkDevicesGroupUpdate?.ok) {
        toast.info(TEXTS.SELECT_MASTER_VIDEO_STREAM_SUCCESS);
      } else {
        toast.error(result?.data?.smartlkDevicesGroupUpdate?.errors?.[0]?.messages?.[0] || DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  useEffect(() => {
    const currentMasterVideoStreamId = data?.smartlkDevicesGroupQuery.masterVideoStream?.id;

    if (currentMasterVideoStreamId) {
      setMasterVideoStreamId(currentMasterVideoStreamId);
    }
  }, [data?.smartlkDevicesGroupQuery.masterVideoStream?.id]);

  const isButtonDisabled = masterVideoStreamId === data?.smartlkDevicesGroupQuery.masterVideoStream?.id;

  const videoStreams = useMemo(() => {
    const idsSet = new Set<string | number>();
    return data?.smartlkDevicesGroupQuery.devices?.reduce((acc, it) => {
      const videoStream = it.point?.videoStream;
      if (videoStream && !idsSet.has(videoStream.id)) {
        idsSet.add(videoStream.id);
        acc.push(videoStream);
      }

      return acc;
    }, [] as IVideoStream[]);
  }, [data?.smartlkDevicesGroupQuery.devices]);

  return {
    isButtonDisabled,
    updatePointsGroup,
    isButtonLoading: mutationResult.loading || loading,
    videoStreams,
    setMasterVideoStreamId,
    masterVideoStreamId,
  };
};
